import { verifyPresentation, VerifiablePresentationV1 } from '@concordium/web-sdk/wasm';
import { VerifiablePresentation } from '@concordium/web-sdk/types';
import { ENDPOINTS, type Fetch, type Network } from './chain.ts';
import type { CompanyProof, PinnedCredential } from './issuer.ts';
import { proofClaims, type ProofClaims } from './proof-request.ts';

/**
 * Verifies the company's Concordium identity proof with `@concordium/web-sdk`
 * (WASM). Loaded lazily by the scanner: only this check needs it.
 *
 * What it needs besides the proof (checked 2026-10-23, see SPEC.md §8.4):
 *  - the cryptographic parameters: wallet-proxy `/v0/global` (CORS `*`);
 *  - the identity provider's and anonymity revokers' keys: wallet-proxy
 *    `/v2/ip_info` (CORS `*`, includes Global FinReg, idp 3);
 *  - for an ACCOUNT credential (V0) also that credential's on-chain
 *    attribute commitments. No CORS-open REST service serves them (CCDScan
 *    and the wallet-proxy do not), so they come from a node over gRPC-web,
 *    from commitments pinned in the app's trust bundle, or, failing both,
 *    Concordium's hosted verifier is asked (and the result says so).
 *
 * Nothing here trusts the issuer's servers.
 */
export type ProofMethod = 'local' | 'local-grpc' | 'local-pinned' | 'hosted' | 'none';

export type ProofVerification = {
  ok: boolean;
  method: ProofMethod;
  /** One plain line on how it was verified. */
  methodLabel: string;
  /** Verified only when `ok`. */
  claims: ProofClaims | null;
  /** The identity provider the verification relied on. */
  idpName: string | null;
  error?: string;
};

export type ProofVerifyOptions = {
  network: Network;
  fetch: Fetch;
  /** The app's pinned credential (trust bundle), for a V0 proof. */
  pinned?: PinnedCredential | null;
  /** Try a public node's gRPC-web for the commitments (default true in a browser). */
  grpcWeb?: { url: string; port: number } | null;
  /** Ask Concordium's hosted verifier as a last resort (default true). */
  hosted?: boolean;
  /** Override the wallet-proxy data (tests, offline caches). */
  globalContext?: unknown;
  ipInfo?: Array<{ ipInfo: { ipIdentity: number; ipDescription?: { name?: string } }; arsInfos: unknown }>;
};

export const GRPC_WEB: Record<Network, { url: string; port: number }> = {
  mainnet: { url: 'https://grpc.mainnet.concordium.software', port: 20000 },
  testnet: { url: 'https://grpc.testnet.concordium.com', port: 20000 },
};

export const HOSTED_VERIFIER: Record<Network, string> = {
  mainnet: 'https://web3id-verifier.mainnet.concordium.software/v0/verify',
  testnet: 'https://web3id-verifier.testnet.concordium.com/v0/verify',
};

const cache = new Map<string, Promise<unknown>>();
function cached<T>(key: string, load: () => Promise<T>): Promise<T> {
  let p = cache.get(key) as Promise<T> | undefined;
  if (!p) {
    p = load();
    p.catch(() => cache.delete(key));
    cache.set(key, p);
  }
  return p;
}

async function getJson(fetchFn: Fetch, url: string): Promise<unknown> {
  const res = await fetchFn(url);
  if (!res.ok) throw new Error(`${url} answered HTTP ${res.status}`);
  return res.json();
}

async function globalContext(o: ProofVerifyOptions): Promise<any> {
  if (o.globalContext) return o.globalContext;
  const j = (await cached(`global:${o.network}`, () => getJson(o.fetch, `${ENDPOINTS[o.network].walletProxy}/v0/global`))) as { value?: unknown };
  return j.value ?? j;
}

async function identityProvider(o: ProofVerifyOptions, idp: number) {
  const list = o.ipInfo ?? ((await cached(`ip:${o.network}`, () => getJson(o.fetch, `${ENDPOINTS[o.network].walletProxy}/v2/ip_info`))) as ProofVerifyOptions['ipInfo'])!;
  const found = list.find((x) => x.ipInfo.ipIdentity === idp);
  if (!found) throw new Error(`identity provider ${idp} is not on ${o.network}`);
  return found;
}

const fail = (method: ProofMethod, methodLabel: string, error: string): ProofVerification => ({ ok: false, method, methodLabel, claims: null, idpName: null, error });

export async function verifyCompanyProof(proof: CompanyProof, o: ProofVerifyOptions): Promise<ProofVerification> {
  let claims: ProofClaims;
  try {
    claims = proofClaims(proof.style, proof.json);
  } catch (e) {
    return fail('none', 'the proof could not be read', (e as Error).message);
  }
  if (claims.network && claims.network !== o.network) return fail('none', 'wrong network', `the proof is for ${claims.network}, the company is on ${o.network}`);

  if (proof.style === 'id-proof') {
    return fail('none', 'no browser verifier exists for account ID proofs', 'an account ID proof (requestIdProof) can only be checked by a Concordium node-backed service; make a verifiable presentation instead');
  }

  if (proof.style === 'v1-identity') {
    const label = "verified on this phone with the identity provider's public keys";
    try {
      if (claims.issuerIdp === null) throw new Error('the proof names no identity provider');
      const ip = await identityProvider(o, claims.issuerIdp);
      const gc = await globalContext(o);
      const vp = VerifiablePresentationV1.fromJSON(JSON.parse(proof.json));
      const r = VerifiablePresentationV1.verify(vp, gc, [{ type: 'identity', ipInfo: ip.ipInfo, arsInfos: ip.arsInfos } as never]);
      if (r.type !== 'success') throw new Error(String((r as { error?: unknown }).error ?? 'the proof does not verify').replace(/^Error: /, ''));
      return { ok: true, method: 'local', methodLabel: label, claims, idpName: ip.ipInfo.ipDescription?.name ?? null };
    } catch (e) {
      return fail('local', label, (e as Error).message);
    }
  }

  // V0: an account credential. Its commitments must come from the chain.
  if (!claims.credId) return fail('none', 'the proof names no account credential', 'not an account credential presentation');
  let source: { method: ProofMethod; label: string; ipIdentity: number; commitments: Record<string, string> } | null = null;
  const errors: string[] = [];
  const grpc = o.grpcWeb === undefined ? GRPC_WEB[o.network] : o.grpcWeb;
  if (grpc) {
    try {
      const { ConcordiumGRPCWebClient } = await import('@concordium/web-sdk/grpc');
      const { CredentialRegistrationId } = await import('@concordium/web-sdk/types');
      const client = new ConcordiumGRPCWebClient(grpc.url, grpc.port, { timeout: 8000 });
      const info = await client.getAccountInfo(CredentialRegistrationId.fromHexString(claims.credId));
      for (const c of Object.values(info.accountCredentials)) {
        const v = c.value;
        if (v.type === 'normal' && v.contents.credId === claims.credId) {
          source = { method: 'local-grpc', label: "verified on this phone; the credential's commitments read from a Concordium node", ipIdentity: v.contents.ipIdentity, commitments: v.contents.commitments.cmmAttributes as Record<string, string> };
        }
      }
      if (!source) errors.push('the node does not know this credential');
    } catch (e) {
      errors.push(`node unreachable (${(e as Error).message.slice(0, 80)})`);
    }
  }
  if (!source && o.pinned && o.pinned.cred_id === claims.credId) {
    source = { method: 'local-pinned', label: "verified on this phone; the credential's on-chain commitments are pinned in the NetID app", ipIdentity: o.pinned.ip_identity, commitments: o.pinned.commitments };
  }
  if (source) {
    try {
      if (claims.issuerIdp !== null && claims.issuerIdp !== source.ipIdentity) throw new Error(`the proof claims identity provider ${claims.issuerIdp}, the credential on chain is from ${source.ipIdentity}`);
      const gc = await globalContext(o);
      const vp = VerifiablePresentation.fromString(proof.json);
      verifyPresentation(vp, gc, [{ type: 'account', issuer: source.ipIdentity, commitments: source.commitments } as never]);
      const ip = await identityProvider(o, source.ipIdentity).catch(() => null);
      return { ok: true, method: source.method, methodLabel: source.label, claims: { ...claims, issuerIdp: source.ipIdentity }, idpName: ip?.ipInfo.ipDescription?.name ?? null };
    } catch (e) {
      return fail(source.method, source.label, (e as Error).message);
    }
  }
  if (o.hosted === false) return fail('none', 'no source for the credential commitments', errors.join('; ') || 'no commitments');
  const label = "verified by Concordium's hosted verifier (this check trusts Concordium)";
  try {
    const res = await o.fetch(HOSTED_VERIFIER[o.network], { method: 'POST', headers: { 'content-type': 'application/json' }, body: proof.json });
    const body = (await res.json()) as unknown;
    if (!res.ok) throw new Error(typeof body === 'string' ? body : `HTTP ${res.status}`);
    const b = body as { challenge?: string; request?: { challenge?: string } };
    const challenge = b.request?.challenge ?? b.challenge;
    if (challenge && challenge !== claims.challenge) throw new Error('the hosted verifier reports another challenge');
    return { ok: true, method: 'hosted', methodLabel: label, claims, idpName: null };
  } catch (e) {
    return fail('hosted', label, `${(e as Error).message}${errors.length ? ` (before: ${errors.join('; ')})` : ''}`);
  }
}
